<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

interface Props {
  selectedIndex?: number;
  index?: number;
  searchTerm?: string | null;
  searchHref?: string | null;
  highlightedHtml?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  selectedIndex: -1,
  index: 0,
  searchTerm: null,
  searchHref: null,
  highlightedHtml: null,
});

const emit = defineEmits<{
  'do-search': [searchTerm: string];
  'save-history': [searchTerm: string];
}>();

const hasFocus = ref(false);

const isSelected = computed(() => props.selectedIndex === props.index);

const normalizedHref = computed(() => {
  if (!props.searchHref) return '';
  return props.searchHref.startsWith('/') || props.searchHref.startsWith('http')
    ? props.searchHref
    : '/' + props.searchHref;
});

const setTerm = (event?: MouseEvent) => {
  if (event) {
    event.preventDefault();
  }

  if (props.searchHref) {
    if (props.searchTerm) {
      emit('save-history', props.searchTerm);
    }
    window.location.href = normalizedHref.value;
  } else if (props.searchTerm) {
    emit('do-search', props.searchTerm);
  }
};

const onKeyResult = (event: KeyboardEvent) => {
  switch (event.code) {
    case 'Enter':
      if (isSelected.value || hasFocus.value) {
        if (props.searchHref) {
          window.location.href = normalizedHref.value;
          break;
        }
        setTerm();
      }
      break;
  }
};

onMounted(() => {
  window.addEventListener('keyup', onKeyResult);
});

onUnmounted(() => {
  window.removeEventListener('keyup', onKeyResult);
});
</script>

<template>
  <li
    class="search__term"
    :class="{ focus: isSelected }"
    tabindex="0"
    @mousedown.prevent="setTerm"
    @click="setTerm"
    @focus="hasFocus = true"
    @blur="hasFocus = false"
  >
    <a v-if="searchHref" :href="normalizedHref" class="anchor" tabindex="-1">
      <span v-if="highlightedHtml" v-html="highlightedHtml"></span>
      <slot v-else></slot>
    </a>
    <template v-else>
      <span v-if="highlightedHtml" v-html="highlightedHtml"></span>
      <slot v-else></slot>
    </template>
  </li>
</template>

<style lang="scss" scoped>
.search__term {
  display: block;
  padding: 5px 10px;
  cursor: pointer;

  &.focus,
  &:hover {
    background-color: #e0e0e0;
  }

  .regular {
    font-weight: normal;
  }
}
</style>
